/**
 * RapprochementView
 * Reconciliation view showing invoice-payment matching status.
 * Summary cards + filterable table + unmatched payments section.
 */

import React, { useState } from 'react';
import {
  MagnifyingGlassIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import { useReconciliation } from '../../hooks/useReconciliation';
import type { ReconciliationInvoice } from '../../hooks/useReconciliation';
import '../../facturation.css';

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('fr-BE', { style: 'currency', currency: 'EUR' }).format(amount);

const formatDate = (date: string) =>
  new Date(date).toLocaleDateString('fr-BE');

type ReconciliationStatus = 'FULLY_PAID' | 'PARTIALLY_PAID' | 'UNPAID' | '';

export const RapprochementView: React.FC = () => {
  const [statusFilter, setStatusFilter] = useState<ReconciliationStatus>('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);

  // Debounce search
  const searchTimeoutRef = React.useRef<ReturnType<typeof setTimeout>>();
  const handleSearchChange = (value: string) => {
    setSearch(value);
    clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearch(value);
      setPage(1);
    }, 400);
  };

  const { data, isLoading } = useReconciliation({
    status: statusFilter,
    search: debouncedSearch,
    page,
  });

  const summary = data?.summary;
  const invoices = data?.invoices || [];
  const unmatchedPayments = data?.unmatchedPayments || [];
  const pagination = data?.pagination;

  const matchRate = summary && summary.invoiceCount > 0
    ? Math.round((summary.paidCount / summary.invoiceCount) * 100)
    : 0;

  const getStatusBadge = (status: ReconciliationInvoice['reconciliation_status']) => {
    switch (status) {
      case 'FULLY_PAID':
        return <span className="fact-rappr-status-badge fully-paid">Paye</span>;
      case 'PARTIALLY_PAID':
        return <span className="fact-rappr-status-badge partially-paid">Partiel</span>;
      case 'UNPAID':
        return <span className="fact-rappr-status-badge unpaid">Impaye</span>;
      default:
        return null;
    }
  };

  if (isLoading) {
    return <div className="fact-rappr-loading">Chargement du rapprochement...</div>;
  }

  return (
    <div className="fact-rappr-container">
      {/* Summary Cards */}
      {summary && (
        <div className="fact-rappr-summary">
          <div className="fact-rappr-card">
            <span className="fact-rappr-card-label">Total facture</span>
            <span className="fact-rappr-card-value blue">
              {formatCurrency(summary.totalInvoiced)}
            </span>
            <span className="fact-rappr-card-sub">{summary.invoiceCount} facture(s)</span>
          </div>
          <div className="fact-rappr-card">
            <span className="fact-rappr-card-label">Total recu</span>
            <span className="fact-rappr-card-value green">
              {formatCurrency(summary.totalReceived)}
            </span>
            <span className="fact-rappr-card-sub">{summary.paidCount} payee(s)</span>
          </div>
          <div className="fact-rappr-card">
            <span className="fact-rappr-card-label">Notes de credit</span>
            <span className="fact-rappr-card-value orange">
              {formatCurrency(summary.totalCredited)}
            </span>
          </div>
          <div className="fact-rappr-card">
            <span className="fact-rappr-card-label">Solde ouvert</span>
            <span className={`fact-rappr-card-value ${summary.netOutstanding > 0 ? 'red' : 'green'}`}>
              {formatCurrency(summary.netOutstanding)}
            </span>
            <span className="fact-rappr-card-sub">Taux: {matchRate}%</span>
          </div>
          {summary.unmatchedPaymentCount > 0 && (
            <div className="fact-rappr-card">
              <span className="fact-rappr-card-label">Paiements non lies</span>
              <span className="fact-rappr-card-value orange">
                {summary.unmatchedPaymentCount}
              </span>
              <span className="fact-rappr-card-sub">
                {formatCurrency(summary.unmatchedPaymentAmount)}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="fact-rappr-filters">
        <button
          className={`fact-rappr-filter-btn ${!statusFilter ? 'active' : ''}`}
          onClick={() => { setStatusFilter(''); setPage(1); }}
        >
          Tous
        </button>
        <button
          className={`fact-rappr-filter-btn ${statusFilter === 'FULLY_PAID' ? 'active' : ''}`}
          onClick={() => { setStatusFilter('FULLY_PAID'); setPage(1); }}
        >
          Payes
        </button>
        <button
          className={`fact-rappr-filter-btn ${statusFilter === 'PARTIALLY_PAID' ? 'active' : ''}`}
          onClick={() => { setStatusFilter('PARTIALLY_PAID'); setPage(1); }}
        >
          Partiels
        </button>
        <button
          className={`fact-rappr-filter-btn ${statusFilter === 'UNPAID' ? 'active' : ''}`}
          onClick={() => { setStatusFilter('UNPAID'); setPage(1); }}
        >
          Impayes
        </button>
        <input
          type="text"
          className="fact-rappr-search"
          placeholder="Rechercher facture, client, affaire..."
          value={search}
          onChange={(e) => handleSearchChange(e.target.value)}
        />
      </div>

      {/* Invoice Table */}
      {invoices.length === 0 ? (
        <div className="fact-rappr-empty">
          <DocumentTextIcon />
          <p>Aucune facture trouvee pour ces criteres.</p>
        </div>
      ) : (
        <>
          <div className="fact-rappr-table">
            <div className="fact-rappr-table-header">
              <div>Numero</div>
              <div>Client</div>
              <div className="align-right">Facture</div>
              <div className="align-right">Credit</div>
              <div className="align-right">Recu</div>
              <div>Statut</div>
              <div>Paie.</div>
            </div>

            {invoices.map((inv) => (
              <div key={inv.id} className="fact-rappr-table-row">
                <div>
                  <span style={{ fontWeight: 600, color: '#1f2937', fontSize: '0.8rem' }}>
                    {inv.invoice_number || 'Brouillon'}
                  </span>
                  {inv.affaire_ids && inv.affaire_ids.length > 0 && (
                    <div style={{ fontSize: '0.7rem', color: '#6b7280' }}>
                      {inv.affaire_ids.join(', ')}
                    </div>
                  )}
                </div>
                <div style={{ fontSize: '0.8rem', color: '#374151' }}>
                  {inv.billing_name}
                </div>
                <div className="align-right" style={{ fontWeight: 600 }}>
                  {formatCurrency(inv.total_tvac)}
                </div>
                <div className="align-right" style={{ color: inv.total_credited > 0 ? '#d97706' : '#9ca3af' }}>
                  {inv.total_credited > 0 ? `-${formatCurrency(inv.total_credited)}` : '-'}
                </div>
                <div className="align-right" style={{ fontWeight: 600, color: inv.total_paid > 0 ? '#059669' : '#9ca3af' }}>
                  {inv.total_paid > 0 ? formatCurrency(inv.total_paid) : '-'}
                </div>
                <div>
                  {getStatusBadge(inv.reconciliation_status)}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                  {inv.payment_count > 0 ? `${inv.payment_count}x` : '-'}
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div className="fact-pagination" style={{ marginTop: '12px' }}>
              <button
                className="fact-page-btn"
                disabled={page <= 1}
                onClick={() => setPage(page - 1)}
              >
                Precedent
              </button>
              <span className="fact-page-info">
                Page {pagination.page} sur {pagination.totalPages}
              </span>
              <button
                className="fact-page-btn"
                disabled={page >= pagination.totalPages}
                onClick={() => setPage(page + 1)}
              >
                Suivant
              </button>
            </div>
          )}
        </>
      )}

      {/* Unmatched Payments */}
      {unmatchedPayments.length > 0 && (
        <div className="fact-rappr-unmatched">
          <div className="fact-rappr-unmatched-title">
            <MagnifyingGlassIcon className="w-4 h-4" />
            Paiements sans facture liee
            <span className="fact-rappr-unmatched-count">{unmatchedPayments.length}</span>
          </div>
          <div className="fact-rappr-unmatched-table">
            <div className="fact-rappr-table-header">
              <div>Date</div>
              <div>Debiteur</div>
              <div className="align-right">Montant</div>
              <div>Affaire</div>
              <div className="align-right">Alloue</div>
            </div>
            {unmatchedPayments.slice(0, 20).map((p, i) => (
              <div key={`${p.payment_id}-${i}`} className="fact-rappr-table-row">
                <div>{formatDate(p.date)}</div>
                <div style={{ fontSize: '0.8rem' }}>{p.debtor_name}</div>
                <div className="align-right" style={{ fontWeight: 600 }}>
                  {formatCurrency(p.amount)}
                </div>
                <div>
                  <span style={{ fontWeight: 600, color: '#1f2937' }}>{p.affaire_id}</span>
                </div>
                <div className="align-right">
                  {formatCurrency(p.amount_allocated)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default RapprochementView;
